import type {
  CellData,
  GridId,
  ScreenMethods,
  ScreenLayout,
  GridMap,
} from "@observe-tech/utils";
import type { SyncStore as Store } from "../../infra/state";

export const createMachineOperations = (
  store: Store,
  validateLayout: (layout: ScreenLayout, gridToUse: GridMap) => ScreenLayout,
  gridToUse: (layout: ScreenLayout) => GridMap,
) => {
  const loadMachines: ScreenMethods["loadMachines"] = async (
    machinesToLoad,
    options,
  ) => {
    const currentLayouts = store.get("screenLayouts") || [];

    // Machines we have positive knowledge about. When the caller does not
    // supply the full set, fall back to the batch itself (legacy behaviour:
    // anything not in the batch is treated as removed).
    const knownMachineIds = new Set(
      options?.knownMachineIds ??
        Object.keys(machinesToLoad).map((id) => Number(id)),
    );

    // A cell is only pruned on positive knowledge that its machine/cage/camera
    // is gone. A machine that is known but absent from this batch (e.g. an
    // unreachable site that has not loaded yet) keeps its cell untouched.
    const validateCameraCell = (
      cellData: CellData<[number]>,
    ): CellData<[number]> => {
      if (!cellData) return null;
      if (!knownMachineIds.has(cellData.machineId)) return null;
      const machine = machinesToLoad[cellData.machineId];
      if (!machine) return cellData;
      const cage = machine.find(
        (c: { cageId: number; cameras: number[] }) =>
          c.cageId === cellData.cageId,
      );
      if (!cage) return null;

      if (!cage.cameras.includes(cellData.cameras[0])) return null;

      return cellData;
    };

    const syncCageCell = (
      cellData: CellData<[number, ...number[]]>,
    ): CellData<[number, ...number[]]> => {
      if (!cellData) return null;
      if (!knownMachineIds.has(cellData.machineId)) return null;
      const machine = machinesToLoad[cellData.machineId];
      if (!machine) return cellData;
      const cage = machine.find(
        (c: { cageId: number; cameras: number[] }) =>
          c.cageId === cellData.cageId,
      );
      if (!cage || cage.cameras.length === 0) return null;

      const validExisting = cellData.cameras.filter((cam) =>
        cage.cameras.includes(cam),
      );
      const newCameras = cage.cameras.filter(
        (cam) => !validExisting.includes(cam),
      );
      const updatedCameras = [...validExisting, ...newCameras];

      return {
        ...cellData,
        cameras: updatedCameras as [number, ...number[]],
      };
    };

    const updatedLayouts = currentLayouts.map((layout) => {
      Object.entries(layout.screenData.cameraGrid).forEach(([key, value]) => {
        layout.screenData.cameraGrid[key as GridId] = validateCameraCell(
          value as CellData<[number]>,
        );
      });

      Object.entries(layout.screenData.cageGrid).forEach(([key, value]) => {
        layout.screenData.cageGrid[key as GridId] = syncCageCell(
          value as CellData<[number, ...number[]]>,
        );
      });

      return layout;
    });

    const existingMachines = store.get("machinesLoaded") || {};
    // Start from existing so machines absent from this batch (e.g. unreachable
    // sites) are preserved rather than dropped.
    const mergedMachines: typeof machinesToLoad = { ...existingMachines };
    for (const [machineIdStr, cages] of Object.entries(machinesToLoad)) {
      const machineId = Number(machineIdStr);
      mergedMachines[machineId] = cages.map((newCage) => {
        const existingMachine = existingMachines[machineId];
        if (!existingMachine) return newCage;
        const existingCage = existingMachine.find(
          (c: { cageId: number; cameras: number[] }) =>
            c.cageId === newCage.cageId,
        );
        if (!existingCage) return newCage;
        const validExisting = existingCage.cameras.filter((cam) =>
          newCage.cameras.includes(cam),
        );
        const added = newCage.cameras.filter(
          (cam) => !validExisting.includes(cam),
        );
        return { ...newCage, cameras: [...validExisting, ...added] };
      });
    }
    // Drop machines that are no longer part of the known configuration.
    for (const machineIdStr of Object.keys(mergedMachines)) {
      if (!knownMachineIds.has(Number(machineIdStr))) {
        delete mergedMachines[Number(machineIdStr)];
      }
    }
    store.set("machinesLoaded", mergedMachines);

    // Validate overlays for all layouts
    const validatedLayouts = updatedLayouts.map((layout) =>
      validateLayout(layout, gridToUse(layout)),
    );
    store.set("screenLayouts", validatedLayouts);

    return validatedLayouts;
  };

  return {
    loadMachines,
  };
};
